import React, { useEffect, useMemo, useState } from 'react';
import { Search, Cpu, ArrowUpRight } from 'lucide-react';
import {
  loadCatalog,
  formatContext,
  formatPrice,
  modelTitle,
  capabilities,
  type CatalogModel,
  type ProviderDetail,
} from './modelCatalog';

const initialProvider = (): string => {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('provider')?.toLowerCase() ?? '';
};

const initialQuery = (): string => {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('q') ?? '';
};

export const ModelsDirectory: React.FC = () => {
  const [models, setModels] = useState<CatalogModel[]>([]);
  const [providers, setProviders] = useState<ProviderDetail[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(initialQuery);
  const [provider, setProvider] = useState(initialProvider);

  useEffect(() => {
    let cancelled = false;
    loadCatalog()
      .then((data) => {
        if (cancelled) return;
        setModels(data.models);
        setProviders(data.providerDetails);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Could not load the model catalogue.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const providerMeta = useMemo(() => {
    const map: Record<string, ProviderDetail> = {};
    for (const p of providers) map[p.name.toLowerCase()] = p;
    return map;
  }, [providers]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return models.filter((m) => {
      if (provider && m.provider.toLowerCase() !== provider) return false;
      if (!needle) return true;
      return (
        m.id.toLowerCase().includes(needle) ||
        m.provider.toLowerCase().includes(needle) ||
        (m.aliases ?? []).some((a) => a.toLowerCase().includes(needle))
      );
    });
  }, [models, q, provider]);

  return (
    <main className="mx-auto max-w-[1800px] px-6 pb-24 pt-28">
      <header className="mb-10">
        <div className="mb-3 flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-white/40">
          <Cpu className="h-4 w-4" /> Model gateway
        </div>
        <h1 className="text-4xl font-bold tracking-tight md:text-5xl">Model directory</h1>
        <p className="mt-4 max-w-2xl text-lg font-light leading-relaxed text-white/60">
          Every model routable through the app.nz OpenAI-compatible gateway. autothinking, auto models,
          automatic failover. Open a model for pricing, context, and usage.
        </p>
        <a href="/evals" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-200 hover:underline">
          Start with independent model evals <ArrowUpRight className="h-4 w-4" />
        </a>
      </header>

      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center">
        <label className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search models, providers, aliases…"
            className="w-full rounded-lg border border-white/10 bg-white/[0.03] py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-white/35 focus:border-white/30 focus:outline-none"
          />
        </label>
        <select
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm text-white focus:border-white/30 focus:outline-none"
        >
          <option value="">All providers</option>
          {providers.map((p) => (
            <option key={p.name} value={p.name.toLowerCase()}>
              {p.displayName} ({p.modelCount})
            </option>
          ))}
        </select>
      </div>

      {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>}

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-xl border border-white/10 bg-white/[0.02]" />
          ))}
        </div>
      ) : (
        <>
          <div className="mb-4 text-xs font-mono text-white/40">{filtered.length} models</div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {filtered.map((m) => {
              const meta = providerMeta[m.provider.toLowerCase()];
              return (
                <a
                  key={m.id}
                  href={`/models/${encodeURIComponent(m.id)}`}
                  className="group flex flex-col rounded-xl border border-white/10 bg-white/[0.02] p-4 transition-colors hover:border-white/25 hover:bg-white/[0.04]"
                >
                  <div className="mb-2 flex items-center gap-2 text-xs font-mono text-white/45">
                    {meta?.logo ? (
                      <img src={meta.logo} alt="" className="h-4 w-4 rounded-sm object-contain" />
                    ) : null}
                    <span>{meta?.displayName ?? m.provider}</span>
                    <ArrowUpRight className="ml-auto h-4 w-4 text-white/20 transition-colors group-hover:text-white/60" />
                  </div>
                  <div className="truncate text-base font-semibold tracking-tight" title={m.id}>
                    {modelTitle(m.id)}
                  </div>
                  <code className="mb-3 truncate text-[11px] text-white/40">{m.id}</code>
                  <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono text-white/50">
                    <span>{formatContext(m.context_window)} ctx</span>
                    {m.input_price_per_1m != null && (
                      <span>
                        {formatPrice(m.input_price_per_1m)}/{formatPrice(m.output_price_per_1m)}
                      </span>
                    )}
                    {capabilities(m).map((c) => (
                      <span key={c} className="rounded border border-white/10 px-1.5 py-0.5 text-white/55">
                        {c}
                      </span>
                    ))}
                  </div>
                </a>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-10 text-center text-sm text-white/45">
              No models match “{q}”.
            </div>
          )}
        </>
      )}
    </main>
  );
};
